import { format, formatDistanceToNow, parseISO, isValid } from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";

export interface DateFormatOptions {
  timezone?: string;
  includeTime?: boolean;
  includeSeconds?: boolean;
  relative?: boolean;
}

// Helper function to get the browser's timezone
export const getBrowserTimezone = (): string => {
  try {
    // Use Intl.DateTimeFormat to detect the browser's timezone
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Fallback to UTC if detection fails
    console.warn("Failed to detect browser timezone, falling back to UTC");
    return "UTC";
  }
};

export const DEFAULT_TIMEZONE = getBrowserTimezone();

export class DateUtils {
  private static getValidTimezone(timezone?: string): string {
    if (!timezone) return DEFAULT_TIMEZONE;

    try {
      // Test if timezone is valid by attempting to format a date with it
      Intl.DateTimeFormat(undefined, { timeZone: timezone });
      return timezone;
    } catch {
      console.warn(
        `Invalid timezone "${timezone}", falling back to ${DEFAULT_TIMEZONE}`
      );
      return DEFAULT_TIMEZONE;
    }
  }

  private static parseDate(
    dateInput: string | Date | null | undefined
  ): Date | null {
    if (!dateInput) return null;

    if (dateInput instanceof Date) {
      return isValid(dateInput) ? dateInput : null;
    }

    if (typeof dateInput === "string") {
      const parsed = parseISO(dateInput);
      return isValid(parsed) ? parsed : null;
    }

    return null;
  }

  static formatDate(
    dateInput: string | Date | null | undefined,
    options: DateFormatOptions = {}
  ): string {
    const date = this.parseDate(dateInput);
    if (!date) return "Invalid Date";

    const timezone = this.getValidTimezone(options.timezone);
    const {
      includeTime = true,
      includeSeconds = false,
      relative = false,
    } = options;

    if (relative) {
      return formatDistanceToNow(date, { addSuffix: true });
    }

    let formatString = "MMM d, yyyy";

    if (includeTime) {
      formatString += includeSeconds ? " h:mm:ss a" : " h:mm a";
    }

    try {
      // Always convert from UTC to target timezone
      return formatInTimeZone(date, timezone, formatString);
    } catch (error) {
      console.error("Date formatting error:", error);
      return format(date, formatString);
    }
  }

  static formatDateTime(
    dateInput: string | Date | null | undefined,
    timezone?: string
  ): string {
    return this.formatDate(dateInput, { timezone, includeTime: true });
  }

  static formatDateOnly(
    dateInput: string | Date | null | undefined,
    timezone?: string
  ): string {
    return this.formatDate(dateInput, { timezone, includeTime: false });
  }

  static formatRelativeTime(
    dateInput: string | Date | null | undefined
  ): string {
    return this.formatDate(dateInput, { relative: true });
  }

  static formatDateForInput(
    dateInput: string | Date | null | undefined,
    timezone?: string
  ): string {
    const date = this.parseDate(dateInput);
    if (!date) return "";

    const validTimezone = this.getValidTimezone(timezone);

    try {
      // Convert UTC to target timezone for input display
      const zonedDate = toZonedTime(date, validTimezone);
      return format(zonedDate, "yyyy-MM-dd'T'HH:mm");
    } catch (error) {
      console.error("Date input formatting error:", error);
      return format(date, "yyyy-MM-dd'T'HH:mm");
    }
  }

  static parseInputToUTC(dateInput: string): string | null {
    if (!dateInput) return null;

    try {
      // The datetime-local input gives us a naive datetime string like "2025-08-26T10:46"
      // We need to treat this as being in the user's timezone and convert to UTC
      
      // JavaScript's new Date() constructor with a string without timezone info
      // treats it as local time, which is exactly what we want
      const localDate = new Date(dateInput);
      
      if (isNaN(localDate.getTime())) {
        throw new Error("Invalid date");
      }
      
      // toISOString() automatically converts to UTC
      return localDate.toISOString();
    } catch (error) {
      console.error("Date parsing error:", error);
      return null;
    }
  }

  static parseDateToUTC(dateInput: string, startOfDay: boolean = true): string | null {
    if (!dateInput) return null;

    try {
      // The date input gives us a string like "2025-08-26"
      // For filtering purposes, we typically want either:
      // - Start of day (00:00:00) for "from" dates
      // - End of day (23:59:59) for "to" dates
      
      // Parse the date components
      const [year, month, day] = dateInput.split('-').map(Number);
      
      if (!year || !month || !day) {
        throw new Error("Invalid date format");
      }
      
      // Create the date at start or end of day in the user's timezone
      const hour = startOfDay ? 0 : 23;
      const minute = startOfDay ? 0 : 59;
      const second = startOfDay ? 0 : 59;
      const millisecond = startOfDay ? 0 : 999;
      
      // Create the date as if it's in the user's local timezone
      const localDate = new Date(year, month - 1, day, hour, minute, second, millisecond);
      
      if (isNaN(localDate.getTime())) {
        throw new Error("Invalid date");
      }
      
      // Return as ISO string (automatically converts to UTC)
      return localDate.toISOString();
    } catch (error) {
      console.error("Date parsing error:", error);
      return null;
    }
  }

  static isToday(
    dateInput: string | Date | null | undefined,
    timezone?: string
  ): boolean {
    const date = this.parseDate(dateInput);
    if (!date) return false;

    const validTimezone = this.getValidTimezone(timezone);
    const now = new Date();

    try {
      const dateInTimezone = formatInTimeZone(date, validTimezone, "yyyy-MM-dd");
      const nowInTimezone = formatInTimeZone(now, validTimezone, "yyyy-MM-dd");

      return dateInTimezone === nowInTimezone;
    } catch {
      return false;
    }
  }

  static isThisWeek(
    dateInput: string | Date | null | undefined,
    timezone?: string
  ): boolean {
    const date = this.parseDate(dateInput);
    if (!date) return false;

    const validTimezone = this.getValidTimezone(timezone);
    const now = new Date();

    try {
      const dateInTimezone = formatInTimeZone(date, validTimezone, "yyyy-ww");
      const nowInTimezone = formatInTimeZone(now, validTimezone, "yyyy-ww");

      return dateInTimezone === nowInTimezone;
    } catch {
      return false;
    }
  }

  static isThisMonth(
    dateInput: string | Date | null | undefined,
    timezone?: string
  ): boolean {
    const date = this.parseDate(dateInput);
    if (!date) return false;

    const validTimezone = this.getValidTimezone(timezone);
    const now = new Date();

    try {
      const dateInTimezone = formatInTimeZone(date, validTimezone, "yyyy-MM");
      const nowInTimezone = formatInTimeZone(now, validTimezone, "yyyy-MM");

      return dateInTimezone === nowInTimezone;
    } catch {
      return false;
    }
  }

  static getTimezoneOffset(timezone?: string): string {
    const validTimezone = this.getValidTimezone(timezone);

    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en", {
        timeZone: validTimezone,
        timeZoneName: "short",
      });

      const parts = formatter.formatToParts(now);
      const timeZonePart = parts.find((part) => part.type === "timeZoneName");
      return timeZonePart?.value || validTimezone;
    } catch {
      return validTimezone;
    }
  }
}

// Export convenience functions for easier usage
export const formatDate = DateUtils.formatDate.bind(DateUtils);
export const formatDateTime = DateUtils.formatDateTime.bind(DateUtils);
export const formatDateOnly = DateUtils.formatDateOnly.bind(DateUtils);
export const formatRelativeTime = DateUtils.formatRelativeTime.bind(DateUtils);
export const formatDateForInput = DateUtils.formatDateForInput.bind(DateUtils);
export const parseInputToUTC = DateUtils.parseInputToUTC.bind(DateUtils);
export const parseDateToUTC = DateUtils.parseDateToUTC.bind(DateUtils);
export const isToday = DateUtils.isToday.bind(DateUtils);
export const isThisWeek = DateUtils.isThisWeek.bind(DateUtils);
export const isThisMonth = DateUtils.isThisMonth.bind(DateUtils);
export const getTimezoneOffset = DateUtils.getTimezoneOffset.bind(DateUtils);

/**
 * Format seconds into a human-readable duration string.
 * Examples: "2h 30m", "3d 5h", "45m", "1w 2d"
 */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) {
    return "-";
  }

  const totalMinutes = Math.floor(seconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);
  const totalWeeks = Math.floor(totalDays / 7);

  const remainingDays = totalDays % 7;
  const remainingHours = totalHours % 24;
  const remainingMinutes = totalMinutes % 60;

  const parts: string[] = [];

  if (totalWeeks > 0) {
    parts.push(`${totalWeeks}w`);
    if (remainingDays > 0) parts.push(`${remainingDays}d`);
  } else if (totalDays > 0) {
    parts.push(`${totalDays}d`);
    if (remainingHours > 0) parts.push(`${remainingHours}h`);
  } else if (totalHours > 0) {
    parts.push(`${totalHours}h`);
    if (remainingMinutes > 0) parts.push(`${remainingMinutes}m`);
  } else if (totalMinutes > 0) {
    parts.push(`${totalMinutes}m`);
  } else if (seconds === 0) {
    parts.push("0");
  } else {
    parts.push("< 1m");
  }

  return parts.join(" ");
}

/**
 * Format hours into a human-readable duration string.
 * Similar to formatDuration but accepts hours instead of seconds.
 */
export function formatHoursDuration(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || hours < 0) {
    return "-";
  }
  return formatDuration(hours * 3600);
}
